/**
 * `DimeCloudTerminal` — the exchange-agnostic cloud layer over `<Terminal>`.
 *
 * Takes ONE resolved `DimeTokenExchange` (which sign-in method won is decided
 * above this layer — see `DimeCloudApp`) and runs the full cloud flow:
 * identity exchange → optional access-code onboarding → server-resolved
 * gateway grant → `<Terminal>` against the gateway-proxy, with fresh-per-
 * handshake DIME JWTs and grant re-resolution when the connection dies.
 *
 * This is the surface a future non-Clerk host uses directly (e.g. Paradex
 * exchanging its own JWT via the upcoming web-api /auth endpoint).
 */
import { useMemo, type ReactNode } from 'react';
import styled from 'styled-components';

import { Terminal } from '../Terminal.js';
import type { TerminalConfig } from '../config.js';
import { createDimeCloudClient, type DimeCloudClient } from './api.js';
import type { DimeEnvironment } from './environments.js';
import type { DimeTokenExchange } from './identity.js';
import { withCloudConnection } from './config.js';
import {
  useDimeConnection,
  type CloudConnection,
  type CloudConnectionState,
} from './useDimeConnection.js';
import { AccessCodeScreen } from './AccessCodeScreen.js';

/**
 * Host `TerminalConfig` minus the connection fields the cloud layer supplies
 * (see `withCloudConnection`).
 */
export type CloudTerminalBaseConfig = Omit<
  TerminalConfig,
  | 'gatewayUrl'
  | 'fallbackPairingToken'
  | 'resolveHandshakeToken'
  | 'sidecar'
  | 'ephemeralConnection'
>;

export interface DimeCloudTerminalProps {
  /** Named backend to target. Default `'production'`. */
  readonly environment?: DimeEnvironment;
  /** Explicit web-api base URL — wins over `environment`. */
  readonly apiUrl?: string;
  /** How this host obtains DIME JWTs (e.g. `createClerkTokenExchange(getToken)`). */
  readonly exchange: DimeTokenExchange;
  /** Host terminal config; the cloud layer fills in the connection fields. */
  readonly config: CloudTerminalBaseConfig;
  /**
   * Override the built-in phase UI (spinners / access-code screen / error +
   * retry). Called for every non-'ready' phase; 'ready' always renders the
   * terminal. Omit ⇒ the packaged defaults.
   */
  readonly renderPhase?: (
    state: CloudConnectionState,
    actions: CloudConnection,
  ) => ReactNode;
  /**
   * Same contract as `TerminalProps.agentEffects`: host nodes rendered inside
   * the agent context (so they can call `useTerminalAgent()` /
   * `useTerminalAuth()`) but outside the visible layout — they should render
   * null into the layout (portals are fine). Forwarded to `<Terminal>` on the
   * 'ready' phase; per-mount, so grant `epoch` remounts re-render it naturally.
   */
  readonly agentEffects?: ReactNode;
  /** Same contract as `TerminalProps.compact`: hide the package top bar for space-constrained embeds. */
  readonly compact?: boolean;
}

export function DimeCloudTerminal({
  environment,
  apiUrl,
  exchange,
  config,
  renderPhase,
  agentEffects,
  compact,
}: DimeCloudTerminalProps): JSX.Element {
  const client: DimeCloudClient = useMemo(
    () => createDimeCloudClient({ environment, apiUrl, exchange }),
    [environment, apiUrl, exchange],
  );
  const cloud = useDimeConnection(client);
  // Sidecar calls (model catalog, session sync) go through the web-api's
  // gateway forward with a DIME JWT — one shared auth source for the session.
  const sidecar = useMemo(() => client.createSidecarAuth(), [client]);
  // Fresh DIME JWT per ws handshake — reconnects after the 5-minute TTL
  // otherwise replay the connect-time token and fail with "invalid token".
  const resolveHandshakeToken = useMemo(
    () => client.createHandshakeTokenResolver(),
    [client],
  );

  const { state } = cloud;
  if (state.phase !== 'ready') {
    if (renderPhase != null) return <>{renderPhase(state, cloud)}</>;
    return <DefaultPhase state={state} actions={cloud} />;
  }

  const terminalConfig = withCloudConnection(
    // The Omit above statically strips the connection fields; the cast simply
    // re-widens for `withCloudConnection`, which overwrites all of them.
    config as TerminalConfig,
    {
      grant: state.grant,
      sidecar,
      resolveHandshakeToken,
      onConnectionDead: cloud.refreshGrant,
    },
  );
  // Keyed on `epoch` so a grant re-resolve (e.g. after the 5-minute DIME JWT
  // expired and a reconnect failed) remounts the terminal and handshakes with
  // fresh credentials instead of looping on the stale token.
  return (
    <Terminal
      key={state.epoch}
      config={terminalConfig}
      agentEffects={agentEffects}
      compact={compact}
    />
  );
}

/** Packaged phase UI: spinners, access-code screen, error + retry. */
function DefaultPhase({
  state,
  actions,
}: {
  readonly state: Exclude<CloudConnectionState, { phase: 'ready' }>;
  readonly actions: CloudConnection;
}): JSX.Element {
  switch (state.phase) {
    case 'resolving':
      return (
        <Centered>
          <Muted>Connecting to your terminal…</Muted>
        </Centered>
      );
    case 'provisioning':
      return (
        <Centered>
          <Muted>Setting up your terminal…</Muted>
        </Centered>
      );
    case 'access-code':
      return (
        <AccessCodeScreen
          error={state.error}
          submitting={state.submitting}
          onSubmit={actions.submitAccessCode}
        />
      );
    case 'unavailable':
      return (
        <Centered>
          <Muted>
            Your terminal is currently unavailable ({state.status}). Please
            contact support.
          </Muted>
        </Centered>
      );
    case 'error':
      return (
        <Centered>
          <ErrorStack>
            <Muted>{state.message}</Muted>
            <RetryButton type="button" onClick={actions.retry}>
              Retry
            </RetryButton>
          </ErrorStack>
        </Centered>
      );
    default: {
      // Compile-time exhaustiveness: adding a phase to `CloudConnectionState`
      // without a case above fails to assign here.
      const unhandled: never = state;
      void unhandled;
      return (
        <Centered>
          <Muted>Something went wrong — please refresh.</Muted>
        </Centered>
      );
    }
  }
}

const Centered = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
`;

const Muted = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const ErrorStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const RetryButton = styled.button`
  padding: 8px 18px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-color-container);
  color: var(--text-primary);
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    border-color: var(--accent-color);
  }
`;
